/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import Moment from 'moment' // Npm: Moment library.
import _ from 'underscore' // Npm: underscore library.
import { useQuery } from '@apollo/client' // Npm: Apollo client.
import { IoMdAdd } from 'react-icons/io' // Npm: React icons.
import { DataGrid } from '@mui/x-data-grid' // Npm: Material UI Data Grid.
import {
  Button,
  Flex,
  Tooltip,
  useBreakpointValue,
  useDisclosure
} from '@chakra-ui/react' // Npm: Chakra UI components.


/*
 * PACKAGES
 */
import DownloadToExcel from 'components/DownloadToExcel'
import TableSpinner from 'components/TableSpinner'
import Modal from 'components/Modal'
import PhoneNumberoDirectoryUpsert from 'components/PhoneNumberoDirectoryUpsert'
import PhoneNumberoDirectoryDropDownOptions from 'components/PhoneNumberoDirectoryDropDownOptions'
import PhoneNumberoDirectoryDelete from 'components/PhoneNumberoDirectoryDelete'


/*
 * STYLES
 */
import {
  buttonStyle
} from './index.style'


/*
 * GRAPHS
 */
import PhoneNumberoDirectoryReadQuery from './__query__/index.phoneNumberoDirectory.read.query'


/*
 * OBJECTS
 */
const Index = () => {
  // Const assignment.
  const _skipDifference = 8
  const _successFlags = Object.React.App.enums.GRAPHQL_SUCCESSFUL_QUERY_FLAGS.enums.map(i => i.key)

  // Hook assignment.
  const [paginationModel, setPaginationModel] = React.useState({
    'pageSize': _skipDifference,
    'page': 0
  })
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({ 'id': false })
  const [filterModel, setFilterModel] = React.useState({ items: [] })
  const [phoneNumberoDirectoryToSearch, setPhoneNumberoDirectoryToSearch] = React.useState('')
  const { 'isOpen': isPhoneNumberoDirectoryCreateOpen, 'onOpen': onPhoneNumberoDirectoryCreateOpen, 'onClose': onPhoneNumberoDirectoryCreateClose } = useDisclosure()
  const { 'isOpen': isPhoneNumberoDirectoryUpdateOpen, 'onOpen': onPhoneNumberoDirectoryUpdateOpen, 'onClose': onPhoneNumberoDirectoryUpdateClose } = useDisclosure()
  const { 'isOpen': isPhoneNumberoDirectoryDeleteOpen, 'onOpen': onPhoneNumberoDirectoryDeleteOpen, 'onClose': onPhoneNumberoDirectoryDeleteClose } = useDisclosure()
  const _QueryPhoneNumberoDirectoryRead = useQuery(PhoneNumberoDirectoryReadQuery, {
    'variables': {
      phoneNumberoDirectoryToSearch,
      'skip': paginationModel?.pageSize * paginationModel?.page,
      'take': paginationModel?.pageSize
    },
    'fetchPolicy': Object.React.App.fetchPolicy,
    'pollInterval': Object.React.App.pollInterval
  })
  const _isFirstLoadCompleted = React.useRef(false)
  const _onSearchInputChange = React.useRef(_.debounce((value) => setPhoneNumberoDirectoryToSearch(value), 800)).current
  const _tableHeaderHeightRef = React.useRef(0)
  const _isCurrentViewMobile = useBreakpointValue({ 'base': 'false', 'md': false, 'lg': false, 'xl': false, 'sm': true, 'xs': true })

  // Data loading check.
  if (!_QueryPhoneNumberoDirectoryRead.loading && 0 < _QueryPhoneNumberoDirectoryRead.data?.PhoneNumberoDirectoryRead?.length && !_isFirstLoadCompleted.current) _isFirstLoadCompleted.current = true
  if (!_.every(_.pluck(_QueryPhoneNumberoDirectoryRead.data?.PhoneNumberoDirectoryRead, 'status'), i => _successFlags.includes(i))) _isFirstLoadCompleted.current = false

  // Extract search from filter model
  React.useEffect(() => {
    const displayNameFilter = filterModel?.items?.find(item => item.field === 'displayName')
    const searchValue = displayNameFilter?.value || ''
    _onSearchInputChange(searchValue)
  }, [filterModel])

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      _onSearchInputChange.cancel?.()
    }
  }, [])

  // Data assignment.
  const columns = React.useMemo(() => [
    {
      'field': 'sno',
      'headerName': 'S.No.',
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'flex': 1,
      'minWidth': 180
    },
    {
      'field': 'id',
      'headerName': 'ID',
      'align': 'center',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'headerAlign': 'center',
      'hide': true
    },
    {
      'field': 'displayName',
      'headerName': 'Display Name',
      'description': 'Display name of phone number directory.',
      'editable': false,
      'sortable': true,
      'filterable': true,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 350
    },
    {
      'field': 'totalPhoneNumberos',
      'headerName': 'Total PhoneNumberos',
      'description': 'Total number of phone numbers.',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'flex': 1,
      'minWidth': 250
    },
    {
      'field': 'createdAt',
      'headerName': 'Created At',
      'description': 'Created date.',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'flex': 1,
      'minWidth': 250
    },
    {
      'field': 'updatedAt',
      'headerName': 'Updated At',
      'description': 'Updated date.',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'disableColumnMenu': true,
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 250
    },
    {
      'field': 'action',
      'headerName': 'Actions',
      'description': 'Actions for phone number directory.',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'width': 150,
      'disableColumnMenu': true,
      'renderCell': ({ row }) => <PhoneNumberoDirectoryDropDownOptions
        phoneNumberoDirectoryId={row?.id}
        onPhoneNumberoDirectoryUpdate={onPhoneNumberoDirectoryUpdateOpen}
        onPhoneNumberoDirectoryDelete={onPhoneNumberoDirectoryDeleteOpen}
      />
    }
  ], [onPhoneNumberoDirectoryUpdateOpen, onPhoneNumberoDirectoryDeleteOpen])

  const rows = React.useMemo(() => _.compact(_QueryPhoneNumberoDirectoryRead?.data?.PhoneNumberoDirectoryRead?.map((r, index) => _successFlags.includes(r.status) ? ({
    'id': r?.id || String.random(9),
    'sno': `${(index + (paginationModel.page * paginationModel.pageSize)) + 1}.`,
    'displayName': r?.displayName ?? '-',
    'totalPhoneNumberos': r?._count?.PhoneNumbero ?? 0,
    'createdAt': Moment(r?.createdAt).format('YYYY-MM-DD'),
    'updatedAt': Moment(r?.updatedAt).format('YYYY-MM-DD')
  }) : null)), [_QueryPhoneNumberoDirectoryRead?.data?.PhoneNumberoDirectoryRead, paginationModel.page, paginationModel.pageSize])

  // Return component.
  return (
    <>
      <Flex className='phoneNumberoDirectory base'>
        <Flex
          display='flex'
          flex={1}
          flexDirection='column'
          gap={_isCurrentViewMobile ? '12px' : '22px'}
          bg='white'
          minH="81vh"
          maxH='81vh'
          borderRadius='20px'
          p={_isCurrentViewMobile ? '12px' : '22px'}>
          <Flex pb='0px' justify='space-between'>
            <Flex gap='12px'>
              <Tooltip label='Create New' fontSize='sm'>
                <Button
                  leftIcon={<IoMdAdd />}
                  onClick={onPhoneNumberoDirectoryCreateOpen}
                  style={buttonStyle}>
                  Create New
                </Button>
              </Tooltip>
            </Flex>
            <DownloadToExcel
              cellsData={0 === _QueryPhoneNumberoDirectoryRead?.data?.PhoneNumberoDirectoryRead?.length || !_.every(_.pluck(_QueryPhoneNumberoDirectoryRead?.data?.PhoneNumberoDirectoryRead, 'status'), i => _successFlags.includes(i)) ? [] : _QueryPhoneNumberoDirectoryRead?.data?.PhoneNumberoDirectoryRead.map((item, __index) => ({
                'S.No.': `${(__index + (paginationModel.pageSize * (paginationModel.page))) + 1}.`,
                'Display Name': item.displayName ?? '-',
                'Total PhoneNumbers': item?._count?.PhoneNumbero ?? '-',
                'CreatedAt': Moment(item?.createdAt).format('YYYY-MM-DD'),
                'UpdatedAt': Moment(item?.updatedAt).format('YYYY-MM-DD')
              }))}
              headersData={[
                'S.No.',
                'Display Name',
                'Total PhoneNumberos',
                'CreatedAt',
                'UpdatedAt'
              ].map(i => ({ 'key': i, 'label': i }))} />
          </Flex>
          <DataGrid
            key={`${paginationModel.page}-${paginationModel.pageSize}`}
            rows={rows}
            columns={columns}
            rowHeight={58}
            rowCount={_.pluck(_QueryPhoneNumberoDirectoryRead?.data?.PhoneNumberoDirectoryRead, '_totalCount')?.[0] || 0}
            paginationMode='server'
            disableColumnSorting={true}
            filterModel={filterModel}
            onFilterModelChange={setFilterModel}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 30, 50, 100]}
            slots={{
              'loadingOverlay': () => <TableSpinner isLoading={true} isEmpty={false} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />,
              'noResultsOverlay': () => <TableSpinner isLoading={false} isEmpty={true} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />,
              'noRowsOverlay': () => <TableSpinner isLoading={false} isEmpty={true} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />
            }}
            disableRowSelectionOnClick
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={newModel => setColumnVisibilityModel(newModel)}
            loading={_QueryPhoneNumberoDirectoryRead?.loading}
          />
        </Flex>
        <Modal
          size='md'
          title='PhoneNumbero Directory Create'
          isOpen={isPhoneNumberoDirectoryCreateOpen}
          onClose={onPhoneNumberoDirectoryCreateClose}>
          <PhoneNumberoDirectoryUpsert isCreateOnly={true} />
        </Modal>
        <Modal
          size='md'
          title='PhoneNumbero Directory Update'
          isOpen={isPhoneNumberoDirectoryUpdateOpen}
          onClose={onPhoneNumberoDirectoryUpdateClose}>
          <PhoneNumberoDirectoryUpsert />
        </Modal>
        <Modal
          size='md'
          title='PhoneNumbero Directory Delete'
          isOpen={isPhoneNumberoDirectoryDeleteOpen}
          onClose={onPhoneNumberoDirectoryDeleteClose}>
          <PhoneNumberoDirectoryDelete />
        </Modal>
      </Flex>
    </>
  )
}


/*
 * EXPORT
 */
Index.propTypes = {}


/*
 * EXPORT
 */
export default Index
